import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

const CompanyDetails = ({ data, onChange, onSubmit }) => {
  const { t, language } = useTranslation();
  const isRtl = language === 'ar';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleOtpChange = (index) => (event) => {
    const value = event.target.value;
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.querySelector(`input[name=otp-${index + 1}]`);
        if (nextInput) nextInput.focus();
      }

      // Update parent component with full OTP string
      onChange('companyDetails')({ otp: newOtp.join('') });
    }
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleTermsChange = (event) => {
    setTermsAccepted(event.target.checked);
    onChange('companyDetails')({ termsAccepted: event.target.checked });
  };

  const handleFieldChange = (field) => (event) => {
    onChange('companyDetails')({ [field]: event.target.value });
  };

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 0,
          borderColor: 'divider',
          '& .MuiInputBase-input': {
            fontSize: '0.8rem',
            textAlign: isRtl ? 'right' : 'left'
          },
          '& .MuiFormHelperText-root': {
            textAlign: isRtl ? 'right' : 'left'
          }
        }}
      >
        <CardContent>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.875rem',
              textAlign: isRtl ? 'right' : 'left'
            }}
          >
            {t('ZatcaOnboarding.CompanyDetails.Title')}
          </Typography>

          <Stack spacing={2}>
            {/* OTP Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}
              >
                {t('ZatcaOnboarding.CompanyDetails.Otp_Code')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  mb: 1,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textAlign: 'center'
                }}
              >
                {t('ZatcaOnboarding.CompanyDetails.Otp_Helper')}
              </Typography>
              {/* Keep OTP 6-box input direction ltr so digit sequence 0..5 is predictable */}
              <Box dir="ltr" sx={{ display: 'flex', gap: 0.5, direction: 'ltr' }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    name={`otp-${index}`}
                    value={digit}
                    onChange={handleOtpChange(index)}
                    onKeyDown={handleKeyDown(index)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: '1rem',
                        padding: '6px',
                        width: '36px',
                        height: '36px'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        '& fieldset': {
                          borderColor: 'divider'
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main'
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Company Details Section */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Common_Name')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.commonName || ''}
                  onChange={handleFieldChange('commonName')}
                  placeholder="TST-886431145-399999999900003"
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.ESG_Serial_Number')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.esgSerialNumber || ''}
                  placeholder={t('ZatcaOnboarding.CompanyDetails.Fetching')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      backgroundColor: '#f5f5f5',
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Organization_Identifier')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.organizationIdentifier || ''}
                  onChange={handleFieldChange('organizationIdentifier')}
                  placeholder="399999999900003"
                  required
                  size="small"
                  helperText={t('ZatcaOnboarding.CompanyDetails.Vat_Helper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Organization_Unit_Name')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.organizationUnitName || ''}
                  onChange={handleFieldChange('organizationUnitName')}
                  placeholder={t('ZatcaOnboarding.CompanyDetails.Placeholders.Org_Unit')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Organization_Name')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.organizationName || ''}
                  onChange={handleFieldChange('organizationName')}
                  placeholder={t('ZatcaOnboarding.CompanyDetails.Placeholders.Org_Name')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Country')}
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={data?.companyDetails?.country || 'SA'}
                  onChange={handleFieldChange('country')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                >
                  <MenuItem value="SA" sx={{ fontSize: '0.8rem', textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Country_SA')}
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Invoice_Type')}
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={data?.companyDetails?.invoiceType || '1100'}
                  onChange={handleFieldChange('invoiceType')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                >
                  <MenuItem value="1000" sx={{ fontSize: '0.8rem', textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Standard_Invoice')}
                  </MenuItem>
                  <MenuItem value="0100" sx={{ fontSize: '0.8rem', textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Simplified_Invoice')}
                  </MenuItem>
                  <MenuItem value="1100" sx={{ fontSize: '0.8rem', textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Both_Invoices')}
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Industry')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.industry || ''}
                  onChange={handleFieldChange('industry')}
                  placeholder={t('ZatcaOnboarding.CompanyDetails.Placeholders.Industry')}
                  required
                  size="small"
                  helperText={t('ZatcaOnboarding.CompanyDetails.Industry_Helper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                  {t('ZatcaOnboarding.CompanyDetails.Location')}
                </Typography>
                <TextField
                  fullWidth
                  value={data?.companyDetails?.location || ''}
                  onChange={handleFieldChange('location')}
                  placeholder="RRRD2929"
                  required
                  size="small"
                  helperText={t('ZatcaOnboarding.CompanyDetails.Location_Helper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 18,
                        },
                        ...(isRtl ? { ml: 1, mr: 0 } : { mr: 1, ml: 0 })
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {t('ZatcaOnboarding.CompanyDetails.Agree_Terms')}
                    </Typography>
                  }
                  sx={{
                    alignItems: 'center',
                    margin: 0,
                    width: '100%'
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    direction: isRtl ? 'rtl' : 'ltr',
                    textAlign: isRtl ? 'right' : 'left',
                    lineHeight: 1.8,
                    fontSize: '0.875rem',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Terms_Title')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRtl ? 'right' : 'left' }}>
                    • {t('ZatcaOnboarding.CompanyDetails.Terms_Point1')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRtl ? 'right' : 'left' }}>
                    • {t('ZatcaOnboarding.CompanyDetails.Terms_Point2')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRtl ? 'right' : 'left' }}>
                    • {t('ZatcaOnboarding.CompanyDetails.Terms_Point3')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: isRtl ? 'right' : 'left' }}>
                    • {t('ZatcaOnboarding.CompanyDetails.Terms_Point4')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: isRtl ? 'right' : 'left' }}>
                    {t('ZatcaOnboarding.CompanyDetails.Terms_Consent')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyDetails;